import { v4 as uuidv4 } from 'uuid';
import type { GetTemplate_template_blueprint } from '@frontend/app/queries/types/GetTemplate';
import { AutomationNodeType } from '@frontend/app/types/globalTypes';
import { AutomationNodeV2, AutomationBlueprintV2, AutomationConditionNodeSubType, AutomationVariableV2 } from '../types';

/**
 * Utility functions for working with automation tree structures
 */

/**
 * Find a node by ID in the automation tree
 */
export function findNodeById(root: AutomationNodeV2, nodeId: string): AutomationNodeV2 | null {
  if (root.id === nodeId) {
    return root;
  }

  for (const child of root.children) {
    const found = findNodeById(child, nodeId);
    if (found) {
      return found;
    }
  }

  // Also search in elseChildren for condition nodes
  if (root.elseChildren) {
    for (const child of root.elseChildren) {
      const found = findNodeById(child, nodeId);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

/**
 * Find the parent node of a given node ID
 */
export function findParentNode(root: AutomationNodeV2, nodeId: string): AutomationNodeV2 | null {
  for (const child of root.children) {
    if (child.id === nodeId) {
      return root;
    }
    const found = findParentNode(child, nodeId);
    if (found) {
      return found;
    }
  }

  // Also check elseChildren
  if (root.elseChildren) {
    for (const child of root.elseChildren) {
      if (child.id === nodeId) {
        return root;
      }
      const found = findParentNode(child, nodeId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * Get all nodes in the tree as a flat array
 */
export function getAllNodes(root: AutomationNodeV2): AutomationNodeV2[] {
  const nodes: AutomationNodeV2[] = [root];

  for (const child of root.children) {
    nodes.push(...getAllNodes(child));
  }

  // Also include elseChildren
  if (root.elseChildren) {
    for (const child of root.elseChildren) {
      nodes.push(...getAllNodes(child));
    }
  }

  return nodes;
}

/**
 * Get the depth of a node in the tree
 */
export function getNodeDepth(root: AutomationNodeV2, targetNodeId: string): number {
  if (root.id === targetNodeId) {
    return 0;
  }

  for (const child of root.children) {
    const depth = getNodeDepth(child, targetNodeId);
    if (depth !== -1) {
      return depth + 1;
    }
  }

  return -1;
}

/**
 * Add a node as a child to a parent node
 */
export function addChildNode(
  root: AutomationNodeV2,
  parentId: string,
  newNode: AutomationNodeV2,
  index?: number,
  isElseBranch?: boolean
): AutomationNodeV2 {
  if (root.id === parentId) {
    if (isElseBranch) {
      // Add to elseChildren
      const newElseChildren = [...(root.elseChildren || [])];
      if (index !== undefined) {
        newElseChildren.splice(index, 0, newNode);
      } else {
        newElseChildren.push(newNode);
      }

      // If this is the first time adding to elseChildren and it's a CONDITION,
      // convert it to IF_ELSE_CONDITION
      let updatedSubType = root.subType;
      if (root.type === 'CONDITION' && root.subType !== AutomationConditionNodeSubType.IF_ELSE_CONDITION) {
        updatedSubType = AutomationConditionNodeSubType.IF_ELSE_CONDITION;
      }

      return {
        ...root,
        subType: updatedSubType,
        elseChildren: newElseChildren,
      };
    } else {
      // Add to children (default behavior)
      const newChildren = [...root.children];
      if (index !== undefined) {
        newChildren.splice(index, 0, newNode);
      } else {
        newChildren.push(newNode);
      }
      return {
        ...root,
        children: newChildren,
      };
    }
  }

  return {
    ...root,
    children: root.children.map((child) => addChildNode(child, parentId, newNode, index, isElseBranch)),
    elseChildren: root.elseChildren?.map((child) => addChildNode(child, parentId, newNode, index, isElseBranch)),
  };
}

/**
 * Remove a node from the tree
 */
export function removeNode(root: AutomationNodeV2, nodeId: string): AutomationNodeV2 | null {
  if (root.id === nodeId) {
    return null; // Can't remove root
  }

  return {
    ...root,
    children: root.children
      .map((child) => removeNode(child, nodeId))
      .filter((child) => child !== null) as AutomationNodeV2[],
    elseChildren: root.elseChildren
      ?.map((child) => removeNode(child, nodeId))
      .filter((child) => child !== null) as AutomationNodeV2[],
  };
}

/**
 * Update a specific node in the tree
 */
export function updateNode(
  root: AutomationNodeV2,
  nodeId: string,
  updates: Partial<AutomationNodeV2>
): AutomationNodeV2 {
  if (root.id === nodeId) {
    return {
      ...root,
      ...updates,
      // Preserve children and elseChildren if not explicitly updated
      children: updates.children || root.children,
      elseChildren: updates.elseChildren !== undefined ? updates.elseChildren : root.elseChildren,
    };
  }

  return {
    ...root,
    children: root.children.map((child) => updateNode(child, nodeId, updates)),
    elseChildren: root.elseChildren?.map((child) => updateNode(child, nodeId, updates)),
  };
}

/**
 * Move a node from one parent to another
 */
export function moveNode(
  root: AutomationNodeV2,
  nodeId: string,
  newParentId: string,
  index?: number
): AutomationNodeV2 {
  const nodeToMove = findNodeById(root, nodeId);
  if (!nodeToMove || nodeId === newParentId) {
    return root;
  }

  // Remove the node from its current location
  const withoutNode = removeNode(root, nodeId);
  if (!withoutNode) {
    return root;
  }

  // Add it to the new parent
  return addChildNode(withoutNode, newParentId, nodeToMove, index);
}

/**
 * Clone a node (deep copy)
 */
export function cloneNode(node: AutomationNodeV2, generateNewIds = true): AutomationNodeV2 {
  const cloned: AutomationNodeV2 = {
    ...node,
    id: generateNewIds ? generateNodeId() : node.id,
    metadata: { ...node.metadata },
    variables: node.variables.map((v) => ({ ...v })),
    children: node.children.map((child) => cloneNode(child, generateNewIds)),
    elseChildren: node.elseChildren?.map((child) => cloneNode(child, generateNewIds)),
  };

  return cloned;
}

/**
 * Add a node to the else branch of a condition node
 */
export function addElseChildNode(
  root: AutomationNodeV2,
  parentId: string,
  newNode: AutomationNodeV2,
  index?: number
): AutomationNodeV2 {
  if (root.id === parentId && root.type === 'CONDITION') {
    const newElseChildren = [...(root.elseChildren || [])];
    if (index !== undefined) {
      newElseChildren.splice(index, 0, newNode);
    } else {
      newElseChildren.push(newNode);
    }
    return {
      ...root,
      elseChildren: newElseChildren,
    };
  }

  return {
    ...root,
    children: root.children.map((child) => addElseChildNode(child, parentId, newNode, index)),
    elseChildren: root.elseChildren?.map((child) => addElseChildNode(child, parentId, newNode, index)),
  };
}

/**
 * Determine if a node is in the else branch of its parent
 */
export function isInElseBranch(root: AutomationNodeV2, nodeId: string): boolean {
  const parent = findParentNode(root, nodeId);
  if (!parent || !parent.elseChildren) {
    return false;
  }
  return parent.elseChildren.some((child) => child.id === nodeId);
}

/**
 * Generate a unique node ID
 */
export function generateNodeId(): string {
  return uuidv4();
}

/**
 * Ensure all nodes in the tree have an id field (required by API)
 * Generates UUID for nodes that don't have one
 */
export function ensureNodeIds(node: AutomationNodeV2): AutomationNodeV2 {
  return {
    ...node,
    id: node.id || generateNodeId(),
    children: node.children.map(ensureNodeIds),
    elseChildren: node.elseChildren?.map(ensureNodeIds),
  };
}

/**
 * Ensure all condition nodes have the correct subType based on their structure
 * Sets subType to IF_ELSE_CONDITION if the condition node has elseChildren
 */
export function ensureConditionSubTypes(node: AutomationNodeV2): AutomationNodeV2 {
  const updatedNode = {
    ...node,
    children: node.children.map(ensureConditionSubTypes),
    elseChildren: node.elseChildren?.map(ensureConditionSubTypes),
  };

  // If this is a CONDITION node with elseChildren, ensure it has the correct subType
  if (updatedNode.type === 'CONDITION' && updatedNode.elseChildren && updatedNode.elseChildren.length > 0) {
    return {
      ...updatedNode,
      subType: AutomationConditionNodeSubType.IF_ELSE_CONDITION,
    };
  }

  return updatedNode;
}

/**
 * Insert a node inline between parent and its existing children
 * This creates a linear chain: Parent → NewNode → ExistingChildren
 */
export function insertNodeInline(
  root: AutomationNodeV2,
  parentId: string,
  newNode: AutomationNodeV2,
  isElseBranch?: boolean
): AutomationNodeV2 {
  if (root.id === parentId) {
    if (isElseBranch) {
      // Move existing elseChildren to become children of the new node
      const newNodeWithChildren = {
        ...newNode,
        children: root.elseChildren || [],
        elseChildren: newNode.elseChildren,
      };

      const updatedRoot = {
        ...root,
        elseChildren: [newNodeWithChildren],
      };

      // Don't set subType here - let ensureConditionSubTypes handle it during save
      return updatedRoot;
    } else {
      // Move existing children to become children of the new node
      const newNodeWithChildren = {
        ...newNode,
        children: root.children,
        elseChildren: newNode.elseChildren,
      };

      return {
        ...root,
        children: [newNodeWithChildren],
      };
    }
  }

  return {
    ...root,
    children: root.children.map((child) => insertNodeInline(child, parentId, newNode, isElseBranch)),
    elseChildren: root.elseChildren?.map((child) => insertNodeInline(child, parentId, newNode, isElseBranch)),
  };
}

/**
 * Validate the automation tree structure
 */
export function validateAutomationTree(root: AutomationNodeV2): string[] {
  const errors: string[] = [];
  const visitedIds = new Set<string>();

  function validateNode(node: AutomationNodeV2, path: string): void {
    // Check for duplicate IDs
    if (visitedIds.has(node.id)) {
      errors.push(`Duplicate node ID found: ${node.id} at ${path}`);
    }
    visitedIds.add(node.id);

    // Check for circular references (basic check)
    if (path.split('/').filter((id) => id === node.id).length > 1) {
      errors.push(`Circular reference detected for node: ${node.id}`);
    }

    // Validate children
    node.children.forEach((child) => {
      validateNode(child, `${path}/${child.id}`);
    });

    // Type-specific validations (relaxed for development)
    switch (node.type) {
      case AutomationNodeType.CONDITION:
        // Allow condition nodes without configuration for now
        // if (!node.metadata.condition) {
        //   errors.push(`Condition node ${node.id} missing condition metadata`);
        // }
        break;
      case AutomationNodeType.ACTION:
        if (!node.subType) {
          errors.push(`Action node ${node.id} missing subType`);
        }
        break;
      case AutomationNodeType.DELAY:
        // Allow delay nodes with default values if not configured
        if (!node.metadata.durationMs) {
          node.metadata.durationMs = 3600000; // Default 1 hour in milliseconds
        }
        break;
    }
  }

  validateNode(root, root.id);
  return errors;
}

// Use existing GraphQL types for templates
type TemplateBlueprint = GetTemplate_template_blueprint;
type TemplateNode = GetTemplate_template_blueprint['triggers'][0]['root'];

/**
 * Convert template blueprint to V2 structure with proper node rendering
 */
export function convertTemplateToV2Blueprint(templateBlueprint: TemplateBlueprint, projectId: number): AutomationBlueprintV2 {
  return {
    id: templateBlueprint.id,
    name: templateBlueprint.name || 'Untitled Automation',
    description: templateBlueprint.description || '',
    triggers: templateBlueprint.triggers?.map((trigger) => ({
      id: trigger.id || generateNodeId(),
      type: trigger.type,
      root: convertTemplateNodeToV2(trigger.root),
      variables: trigger.variables?.map((variable): AutomationVariableV2 => ({
        id: variable.id,
        name: variable.name,
        type: variable.type,
        value: variable.value || null,
      })) || [],
      metadata: trigger.metadata || {},
    })) || [],
    programId: projectId,
    templateId: templateBlueprint.templateId,
    metadata: templateBlueprint.metadata || { icon: 'workflow' },
  };
}

/**
 * Convert template node to V2 structure with proper nesting and IDs
 */
function convertTemplateNodeToV2(templateNode: TemplateNode): AutomationNodeV2 {
  if (!templateNode) {
    throw new Error('Template node is required');
  }

  return {
    id: templateNode.id || generateNodeId(),
    type: templateNode.type,
    subType: templateNode.subType || null,
    children: Array.isArray(templateNode.children)
      ? templateNode.children.map(convertTemplateNodeToV2)
      : [],
    // Handle else children for condition nodes (not present in GraphQL schema)
    elseChildren: undefined,
    metadata: {
      ...templateNode.metadata,
      // Ensure proper metadata structure for different node types
      ...(templateNode.type === AutomationNodeType.DELAY && templateNode.metadata?.durationMs !== undefined
        ? { durationMs: templateNode.metadata.durationMs }
        : {}),
      ...(templateNode.type === AutomationNodeType.CONDITION && templateNode.metadata?.condition
        ? { condition: templateNode.metadata.condition }
        : {}),
      ...(templateNode.type === AutomationNodeType.ACTION
        ? {
            resourceId: templateNode.metadata?.resourceId || null,
            templateId: templateNode.metadata?.templateId || null,
            fromPreviousSender: templateNode.metadata?.fromPreviousSender || false,
            boostId: templateNode.metadata?.boostId || null,
          }
        : {}),
    },
    variables: templateNode.variables?.map?.((variable): AutomationVariableV2 => ({
      id: variable.id,
      name: variable.name,
      type: variable.type,
      value: variable.value || null,
    })) || [],
  };
}
